import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import useDataBean from "../../../../../core/services/dataBean/useDataBean";
import { _DOCDocumentVersion, _single } from "../../../../../core/services/dataBean/EntityCatalog";
import { BasicButton } from "../../../../theme/Component/BasicButton";
import { BasicMultiSelectTable, MASK_DATE_DDMMMYYYY, MASK_NUMBER } from "../../../../theme/Component/BasicMultiSelecTable";
import { FileInput } from "../../../../theme/Component/FileInput";
import { Modal } from "../../../../theme/Component/Modal";
import useModal, { MODAL_LG, MODAL_md, MODAL_XL } from "../../../../theme/Component/hooks/useModal";
import usePaginator from "../../../../theme/Component/usePaginator";
import { PrintJson } from "../../../../theme/Component/PrintJson";
import { RenderForm } from "../../../../theme/Component/RenderForm";
import { BasicTable, MASK_DATE_YYYYMMDD_HHMM } from "../../../../theme/Component/BasicTable";
import { ListMenu } from "../../../../theme/Component/ListMenu";
import { ModalConfirm } from "../../../../theme/Component/ModalConfirm";
import { VisorAnyDocument } from "../../../../documents/VisorAnyDocument";
import { FileService } from "../../../../../core/services/FileService";

interface DocumentVersionsTableProps {
    fileRecordId: number | null;
    fileRecordDocumentId: number | null;
    fileRecordDocument: Record<string, any> | null;
    repositoryName: string;
}

export const DocumentVersionsTable = ({
    fileRecordId,
    fileRecordDocumentId,
    fileRecordDocument,
    repositoryName,
}: DocumentVersionsTableProps) => {
    const { instance, setLista, lista, bean, setBean, addRequiredFields, validateForm, isRequired, isMissing, } = useDataBean(_DOCDocumentVersion);

    const paginadorDocumentVersion = usePaginator();
    const fileService = new FileService();
    const modalCreateDocumentVersion = useModal("Cargar documento", MODAL_LG);

    const [selectedDocumentVersions, setSelectedDocumentVersions] = useState<any[]>([]);
    const [isMetadataOpen, setIsMetadataOpen] = useState(false);
    const [documentMetadata, setDocumentMetadata] = useState<Record<string, any>>({});

    const head = [
        { title: "Nombre", property: "FileName", visible: true },
        { title: "Version", property: "CurrentVersion", visible: true, mask: MASK_NUMBER },
        { title: "Tamaño", property: "ByteTransform", visible: true },
        { title: "Fecha reciente", property: "VersionSince", visible: true, mask: MASK_DATE_YYYYMMDD_HHMM },
    ];

    useEffect(() => {
        addRequiredFields(["Title", "Media", "MediaContext"]);
    }, []);

    useEffect(() => {
        if (!fileRecordDocumentId) {
            setLista([]);
            setSelectedDocumentVersions([]);
            return;
        }

        getDocumentVersion(fileRecordDocumentId);
    }, [fileRecordDocumentId, paginadorDocumentVersion.page]);

    const getDocumentVersion = (idDOCFileRecordDocument: number) => {
        instance.getDOCDocumentVersionRender(paginadorDocumentVersion.page, 15, { IDDOCFileRecordDocument: idDOCFileRecordDocument }).then(
            (resp) => {
                paginadorDocumentVersion.setPages(resp.NumberOfPages ?? 1);
                paginadorDocumentVersion.setRecords((resp.NumberOfPages ?? 1) * 15);
                paginadorDocumentVersion.setRegiPerPage(15);
                resp.List.forEach(element => {
                    const doc = element.DataBeanProperties;

                    doc.ByteTransform = formatFileSize(doc.FileSize);
                    doc.URLDownload = doc.StorageMedia && doc.StorageMediaContext ? fileService.getUrlFile(doc.StorageMediaContext, doc.StorageMedia) : "";
                });
                setLista(resp.List ?? []);
                setSelectedDocumentVersions([]);
            }
        ).catch(err => {
            setLista([]);
            setSelectedDocumentVersions([]);
            toast.error(err);
        });
    };

    function formatFileSize(bytes) {
        const size = Number(bytes);

        if (!Number.isFinite(size) || size < 0) {
            return "0 B";
        }

        if (size < 1024) {
            return `${size} B`;
        }

        if (size < 1024 ** 2) {
            return `${(size / 1024).toFixed(2)} KB`;
        }

        if (size < 1024 ** 3) {
            return `${(size / 1024 ** 2).toFixed(2)} MB`;
        }

        return `${(size / 1024 ** 3).toFixed(2)} GB`;
    }

    const openCreateDocumentVersion = () => {
        if (!fileRecordDocumentId) {
            toast.error("Debe seleccionar un documento del carrusel");
            return;
        }

        setBean({ Title: "", DocumentDescription: "", Media: "", MediaContext: "" });
        setDocumentMetadata({});
        setIsMetadataOpen(false);
        modalCreateDocumentVersion.open();
    };

    const submitCreateDocumentVersion = () => {
        if (!validateForm()) {
            toast.error("Faltan Campos obligatorios por Diligenciar")
            return;
        }

        if (!fileRecordDocumentId) {
            toast.error("Debe seleccionar un documento del expediente");
            return;
        }

        instance.addFileToRecordDocument(_single.getAccountID(), fileRecordDocumentId, String(bean.Title).trim(), String(bean.DocumentDescription ?? "").trim(), bean.Media, bean.MediaContext, documentMetadata).then(
            () => {
                getDocumentVersion(fileRecordDocumentId);
                modalCreateDocumentVersion.close();

            }
        ).catch(err => {
            toast.error(err);
        });
    };

    const modal = useModal("Ver documento Adjunto", MODAL_XL)
    const modalC = useModal("Eliminar Documento", MODAL_md)

    const listaBotones = () => {
        const botones = [
            { titulo: 'Ver Documento', icono: 'ri-file-zip-line', visible: bean.URLDownload !== "", evento: () => { modal.open() } },
            { titulo: 'Eliminar documento', icono: 'ri-delete-bin-line', visible: true, evento: () => { modalC.open() } },
            
        ]
        return (
            <ListMenu listaBotones={botones} />
        )
    }

    return (
        <>
            <section>
                <div className="document-file-manager__section">
                    <h2 className="document-file-manager__heading">{repositoryName}</h2>
                    {fileRecordDocumentId &&
                        <BasicButton icon="ri-upload-2-line" eventClick={openCreateDocumentVersion} clase="primary" timeStopClickSeg={250}>
                            Cargar documento
                        </BasicButton>
                    }
                </div>

                {fileRecordDocumentId ?
                    <BasicTable
                        head={head}
                        body={lista}
                        rowSet={setBean}
                        records={paginadorDocumentVersion}
                        listButtons={listaBotones()}
                    />
                    :
                    <div className="document-file-manager__empty">
                        {fileRecordId ? "Seleccione un documento del carrusel." : "Seleccione un expediente del repositorio."}
                    </div>
                }
            </section>

            <Modal modal={modal.modal} updateModal={modal.setModal} >
                <VisorAnyDocument url={bean.URLDownload} type={bean.Extension} />
            </Modal>

            <ModalConfirm modal={modalC.modal} updateModal={modalC.setModal} eventModal={() => { }} >
                <span>Eliminar Documento <strong>{bean.Name} </strong></span>
            </ModalConfirm>

            <Modal modal={modalCreateDocumentVersion.modal} updateModal={modalCreateDocumentVersion.setModal} eventModal={submitCreateDocumentVersion} onSubmitLabel="Cargar">
                <div className="document-version-upload-form">
                    {fileRecordDocument?.DataBeanProperties?.FormCode &&
                        <section className="document-version-upload-form__section document-version-upload-form__section--additional">
                            <button
                                type="button"
                                className="document-version-upload-form__accordion-trigger"
                                aria-expanded={isMetadataOpen}
                                aria-controls="document-version-metadata-content"
                                onClick={() => setIsMetadataOpen((isOpen) => !isOpen)}
                            >
                                <span className="document-version-upload-form__section-icon" aria-hidden="true">
                                    <i className="ri-file-upload-line"></i>
                                </span>
                                <span className="document-version-upload-form__accordion-title">
                                    <strong>Datos del documento</strong>
                                    <small>Complete la información específica requerida</small>
                                </span>
                                <i
                                    className={`document-version-upload-form__accordion-chevron ${isMetadataOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}
                                    aria-hidden="true"
                                ></i>
                            </button>

                            {isMetadataOpen &&
                                <div id="document-version-metadata-content" className="document-version-upload-form__accordion-content">
                                    <RenderForm
                                        idFormType={fileRecordDocument.DataBeanProperties.FormCode}
                                        data={documentMetadata}
                                        handleUpdate={(data) => {
                                            setDocumentMetadata(data);
                                            setIsMetadataOpen(false);
                                        }}
                                        modeTest={false}
                                    />
                                </div>
                            }
                        </section>
                    }

                    <section className="document-version-upload-form__section">

                        <div className="row">
                            <div className="col-md-6">
                                <label className={`${isMissing("Media") || isMissing("MediaContext") ? "has-error" : ""}`}>
                                    Archivo {isRequired("Media") ? <span className="text-danger">*</span> : ""}
                                </label>
                                <FileInput
                                    label=""
                                    directory="gdoc"
                                    docAcept={fileRecordDocument?.DataBeanProperties?.MimeTypes ?? ""}
                                    onCreate={(file: any) => {
                                        setBean({
                                            ...bean,
                                            Title: file.Name,
                                            Media: file.Media,
                                            MediaContext: file.MediaContext,
                                        });
                                    }}
                                />
                            </div>
                            <div className="col-md-6">
                                <label className={`${isMissing("Title") ? "has-error" : ""}`}>
                                    Nombre {isRequired("Title") ? <span className="text-danger">*</span> : ""}
                                </label>
                                <input
                                    className="form-control"
                                    type="text"
                                    value={bean.Title ?? ""}
                                    onChange={(event) => setBean({ ...bean, Title: event.target.value })}
                                />
                            </div>
                            <div className="col-md-12">
                                <label>Descripción</label>
                                <textarea
                                    className="form-control"
                                    value={bean.DocumentDescription ?? ""}
                                    onChange={(event) => setBean({ ...bean, DocumentDescription: event.target.value })}
                                />
                            </div>
                        </div>
                    </section>
                </div>
            </Modal>
        </>
    );
};
